<template>
  <section class="page-hero-about" :class="{ 'page-hero-about--dark': textVariant === 'dark' }">
    <div class="page-hero-about-background">
      <div v-if="showOverlay" class="page-hero-about-overlay" />
      <div
        v-if="backgroundImage"
        class="page-hero-about-image"
        :style="backgroundPositionY ? { '--hero-position-y': backgroundPositionY } : {}"
      >
        <picture v-if="backgroundImageMobile">
          <source :srcset="backgroundImageMobile" media="(max-width: 767px)">
          <img :src="backgroundImage" :alt="title">
        </picture>
        <img v-else :src="backgroundImage" :alt="title">
      </div>
    </div>
    <div class="page-hero-about-wrapper">
      <div class="page-hero-about-container">
        <div class="page-hero-about-content">
          <h1 class="page-hero-about-title">{{ title }}</h1>
          <IndexBreadcrumbs
            v-if="breadcrumbsLabel"
            :current-label="breadcrumbsLabel"
            class="page-hero-about-breadcrumbs"
          />
          <p v-if="subtitle" class="page-hero-about-subtitle">{{ subtitle }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
interface Props {
  /** Заголовок */
  title: string
  /** Підзаголовок (опційно, для категорій курсів) */
  subtitle?: string
  /** Візуальний варіант тексту (за замовчуванням білий) */
  textVariant?: 'light' | 'dark'
  /** Показувати градієнтний оверлей поверх фону */
  showOverlay?: boolean
  /** URL фонового зображення */
  backgroundImage?: string
  /** URL фонового зображення для мобільних (max-width: 767px) */
  backgroundImageMobile?: string
  /** Вертикальне зміщення фону: наприклад "30%", "70%", "top" */
  backgroundPositionY?: string
  /** Підписи для хлібних крихт */
  breadcrumbsLabel?: string
}

withDefaults(defineProps<Props>(), {
  subtitle: undefined,
  textVariant: 'light',
  showOverlay: true,
  backgroundImage: '/assets/images/about/about-hero-bg.jpg',
  backgroundImageMobile: '/assets/images/about/banner_about_mobile.jpg',
  backgroundPositionY: undefined,
  breadcrumbsLabel: undefined,
})
</script>

<style scoped>
.page-hero-about {
  width: 100%;
  position: relative;
  overflow: hidden;
  min-height: 425px;
}

.page-hero-about-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.page-hero-about-image {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.page-hero-about-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center var(--hero-position-y, top);
}

.page-hero-about-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(270deg, rgba(255, 173, 46, 0.06) 31%, rgba(23, 218, 149, 1) 64%, rgba(9, 175, 234, 1) 89%);
  z-index: 2;
}

@media (max-width: 767px) {
  .page-hero-about {
    min-height: 400px;
  }

  .page-hero-about-overlay {
    background: linear-gradient(0deg, rgba(255, 173, 46, 0.06) 31%, rgba(23, 218, 149, 1) 64%, rgba(9, 175, 234, 1) 89%);
  }

  .page-hero-about-image img {
    object-position: center top;
  }
}
.page-hero-about-wrapper {
  position: relative;
  z-index: 3;
  max-width: var(--max-width-container);
  margin: 0 auto;
}

.page-hero-about-container {
  width: 100%;
  max-width: var(--max-width-container);
  margin: 0 auto;
  padding: 160px 50px 96px;
}

.page-hero-about-content {
  max-width: 650px;
}

.page-hero-about-title {
  font-family: var(--font-inter);
  font-weight: 600;
  font-size: 60px;
  line-height: 1.2;
  color: #fff;
  margin: 0;
}

.page-hero-about-breadcrumbs {
  margin-top: 18px;
}

.page-hero-about-subtitle {
  font-family: var(--font-inter);
  font-size: 20px;
  font-weight: 400;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.95);
  margin: 12px 0 0 0;
}

.page-hero-about--dark .page-hero-about-title,
.page-hero-about--dark .page-hero-about-subtitle {
  color: #252840;
}

@media (max-width: 1400px) {
  .page-hero-about-container {
    padding: 140px 50px 80px;
  }

  .page-hero-about-title {
    font-size: 54px;
  }
}

@media (max-width: 991px) {
  .page-hero-about-container {
    padding: 120px 30px 60px;
  }

  .page-hero-about-title {
    font-size: 40px;
  }

  .page-hero-about-subtitle {
    font-size: 18px;
  }
}

@media (max-width: 767px) {

  .page-hero-about-subtitle {
    font-size: 16px;
    text-align: center;
  }
  .page-hero-about-container {
    padding: 42px 20px 50px;
  }
  .page-hero-about-title {
    text-align: center;
    font-size: 35px;
  }
}
</style>
